import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  deleteActeur,
  getAllActeursListWithGroup,
  getUsersForActeurLink,
} from "../../../../services/acteursServices";

import { getGroupsList } from "../../../../services/groupServices";

import { PAGE_SIZE } from "./../data/acteurAdmin.constants";

import {
  matchesActeurSearch,
  sortActeurs,
} from "../utils/acteursAdmin.utils";

function useDebouncedValue(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] =
    useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      window.clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function useActeursAdmin(
  addNotification,
) {
  const [acteurs, setActeurs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroupId, setSelectedGroupId] =
    useState("");
  const [linkFilter, setLinkFilter] = useState("all");

  const [page, setPage] = useState(1);

  const [isLoading, setIsLoading] =
    useState(false);
  const [isDeleting, setIsDeleting] =
    useState(false);

  const debouncedSearch = useDebouncedValue(
    searchTerm,
    300,
  );

  const refreshActeurs = useCallback(async () => {
    try {
      const data =
        await getAllActeursListWithGroup();

      setActeurs(
        Array.isArray(data) ? data : [],
      );
    } catch (error) {
      console.error(
        "Erreur lors du chargement des acteurs :",
        error,
      );

      setActeurs([]);

      addNotification?.(
        "Erreur lors du chargement des acteurs.",
        "error",
      );
    }
  }, [addNotification]);

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);

      /*
       * Promise.allSettled permet de conserver les données
       * qui ont été chargées correctement si l'un des trois
       * appels échoue.
       */
      const [
        acteursResult,
        groupsResult,
        usersResult,
      ] = await Promise.allSettled([
        getAllActeursListWithGroup(),
        getGroupsList(),
        getUsersForActeurLink(),
      ]);

      if (acteursResult.status === "fulfilled") {
        setActeurs(
          Array.isArray(acteursResult.value)
            ? acteursResult.value
            : [],
        );
      } else {
        setActeurs([]);

        addNotification?.(
          "Erreur lors du chargement des acteurs.",
          "error",
        );
      }

      if (groupsResult.status === "fulfilled") {
        setGroups(
          Array.isArray(groupsResult.value)
            ? groupsResult.value
            : [],
        );
      } else {
        setGroups([]);

        addNotification?.(
          "Erreur lors du chargement des groupes.",
          "error",
        );
      }

      if (usersResult.status === "fulfilled") {
        setUsers(
          Array.isArray(usersResult.value)
            ? usersResult.value
            : [],
        );
      } else {
        setUsers([]);

        addNotification?.(
          "Les utilisateurs n’ont pas pu être chargés.",
          "error",
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const filteredActeurs = useMemo(() => {
    return [...acteurs]
      .filter((acteur) =>
        matchesActeurSearch(
          acteur,
          debouncedSearch,
        ),
      )
      .filter((acteur) => {
        if (!selectedGroupId) return true;

        return acteur?.Groups?.some(
          (group) =>
            String(group.id) ===
            String(selectedGroupId),
        );
      })
      .filter((acteur) => {
        if (linkFilter === "linked") {
          return acteur?.Users?.length > 0;
        }

        if (linkFilter === "unlinked") {
          return !acteur?.Users?.length;
        }

        return true;
      })
      .sort(sortActeurs);
  }, [
    acteurs,
    debouncedSearch,
    selectedGroupId,
    linkFilter,
  ]);

  /*
   * Lorsqu'un filtre change, on revient
   * automatiquement à la première page.
   */
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    selectedGroupId,
    linkFilter,
  ]);

  const pageCount = Math.max(
    1,
    Math.ceil(
      filteredActeurs.length / PAGE_SIZE,
    ),
  );

  const safePage = Math.min(page, pageCount);

  const paginatedActeurs = useMemo(() => {
    const start =
      (safePage - 1) * PAGE_SIZE;

    return filteredActeurs.slice(
      start,
      start + PAGE_SIZE,
    );
  }, [filteredActeurs, safePage]);

  const stats = useMemo(() => {
    const total = acteurs.length;

    const withGroup = acteurs.filter(
      (acteur) => acteur?.Groups?.length > 0,
    ).length;

    const linked = acteurs.filter(
      (acteur) => acteur?.Users?.length > 0,
    ).length;

    return {
      total,
      withGroup,
      linked,
      unlinked: total - linked,
    };
  }, [acteurs]);

  const removeActeur = useCallback(
    async (acteurId) => {
      const confirmed = window.confirm(
        "Êtes-vous sûr de vouloir supprimer cet acteur ?",
      );

      if (!confirmed) return false;

      try {
        setIsDeleting(true);

        await deleteActeur(acteurId);

        setActeurs((previousActeurs) =>
          previousActeurs.filter(
            (acteur) =>
              acteur.id !== acteurId,
          ),
        );

        addNotification?.(
          "Acteur supprimé avec succès.",
          "success",
        );

        return true;
      } catch (error) {
        console.error(
          "Erreur lors de la suppression de l’acteur :",
          error,
        );

        addNotification?.(
          "Erreur lors de la suppression de l’acteur.",
          "error",
        );

        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [addNotification],
  );

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedGroupId("");
    setLinkFilter("all");
    setPage(1);
  };

  return {
    acteurs,
    groups,
    users,

    stats,
    filteredActeurs,
    paginatedActeurs,

    searchTerm,
    setSearchTerm,
    debouncedSearch,

    selectedGroupId,
    setSelectedGroupId,

    linkFilter,
    setLinkFilter,

    page: safePage,
    setPage,
    pageCount,

    isLoading,
    isDeleting,

    refreshActeurs,
    removeActeur,
    resetFilters,
  };
}